<script setup lang="ts">
import { useQuery } from '@urql/vue';
import isUndefined from 'lodash/isUndefined';
import { graphql } from '@/gql';
import { type MasterExperienceRecordType } from '@/gql/graphql';
import { formatExperienceRecordRange } from '../../format';

const props = defineProps<{
  masterId: number;
}>();

const { data: queryData, fetching } = useQuery({
  query: graphql(/* GraphQL */ `
    query MasterExperienceTab_MasterQuery($id: Int!) {
      master(id: $id) {
        id
        specializations {
          id
          yearsOfExperience
          specialization {
            id
            name
          }
        }
        experienceRecords {
          id
          type
          name
          startYear
          endYear
          certificatePhoto {
            id
            url
          }
        }
      }
    }
  `),
  variables: toRef(() => ({ id: props.masterId })),
});
const records = toRef(() => queryData.value?.master.experienceRecords ?? []);
const specializations = toRef(
  () => queryData.value?.master.specializations ?? []
);
const currentYear = new Date().getFullYear();

function groupSpecializations() {
  return specializations.value.length
    ? {
        name: 'Специальности',
        records: specializations.value.map((specialization) => ({
          id: specialization.id + 10000000, // Prevent ID collision with experience records
          name: specialization.specialization.name,
          startYear: currentYear - specialization.yearsOfExperience,
          endYear: currentYear,
          certificatePhoto: undefined,
        })),
      }
    : undefined;
}
function groupExperienceRecords(
  type: MasterExperienceRecordType,
  name: string
) {
  const group = records.value.filter((record) => record.type === type) ?? [];
  return group.length > 0 ? { name, records: group } : undefined;
}

const recordGroups = computed(() =>
  [
    groupSpecializations(),
    groupExperienceRecords('Education', 'Образование'),
    groupExperienceRecords('Training', 'Повышение квалификации'),
    groupExperienceRecords('WorkExperience', 'Опыт работы'),
  ].filter((group): group is NonNullable<typeof group> => !isUndefined(group))
);
</script>

<template>
  <AppLoader v-if="fetching" />
  <div v-else-if="records.length" class="info d-flex flex-column gap-6">
    <div
      v-for="(group, groupIndex) in recordGroups"
      :key="groupIndex"
      class="page-card p-6 d-flex flex-column gap-6"
    >
      <h2 class="m-0">{{ group.name }}</h2>
      <AppSeparator />
      <div class="d-flex flex-column gap-3">
        <div
          v-for="(record, recordIndex) in group.records"
          :key="recordIndex"
          class="info__item"
        >
          <div>
            {{ record.name }}
            <AppButton
              v-if="record.certificatePhoto"
              variant="link"
              class="d-block"
            >
              Смотреть сертификат
            </AppButton>
          </div>
          <span class="info__date">
            {{ formatExperienceRecordRange(record.startYear, record.endYear) }}
          </span>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="stub">Мастер не оставил информацию о карьере</div>
</template>

<style scoped lang="scss">
@import '@glow/shared/assets/core.scss';

.info {
  &__item {
    display: flex;
    justify-content: space-between;
    padding: stretch-size(375, 1920, 12, 20);
    background: var(--app-color-white-secondary);
    border-radius: 16px;

    @include media-breakpoint-down(sm) {
      border-radius: 12px;
    }
  }

  &__date {
    color: var(--app-color-black-secondary);
  }
}
</style>
